import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, MapPin, Bed, Check, Loader2 } from "lucide-react";
import { GOLF_GETAWAY_PACKAGES } from "@/data/golfGetawayPackages";
import { getAllgateway } from "@/services/gatewayService";
import SiteMasthead from "@/cms/SiteMasthead";
import { useEffect, useState } from "react";

export default function GolfGetaways() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [getaways, setGetaways] = useState([]);

  const [loading, setLoading] = useState(true);

  const prefillQuery = new URLSearchParams();

  const date = searchParams.get("date");

  const players = searchParams.get("players");

  if (date) prefillQuery.set("date", date);

  if (players) prefillQuery.set("players", players);

  const prefillSuffix = prefillQuery.toString()
    ? `&${prefillQuery.toString()}`
    : "";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result = await getAllgateway();
        const sorted = Array.isArray(result)
          ? [...result].sort((a, b) => {
              if (a.rounds == null) return 1;
              if (b.rounds == null) return -1;
              return a.rounds - b.rounds;
            })
          : [];
        setGetaways(sorted);
      } catch (err) {
        console.error("Failed to load gateways/courses:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8F7] flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SiteMasthead />

      <div className="pt-8 px-4 pb-12">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-gray-400 mb-6">
            Showing {getaways.length} Golf Getaway packages
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {getaways.map((pkg, i) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
                className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm flex flex-col group hover:shadow-md transition-shadow"
              >
                {/* Image */}
                <div className="relative h-52 shrink-0 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.destination}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <span className="absolute top-3 right-3 bg-white text-[#0B2421] text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                    {pkg.custom
                      ? "Custom"
                      : `${pkg.rounds} Round${pkg.rounds > 1 ? "s" : ""}`} 
                  </span>
                  <div className="absolute bottom-3 left-4">
                    <p className="text-white/60 text-[10px] uppercase tracking-wide">
                      From
                    </p>
                    <p className="text-white font-bold text-2xl leading-none">
                      SGD {pkg.price}
                      <span className="text-white/60 text-xs font-normal">
                        {" "}
                        Per Person
                      </span>
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">
                      {pkg.destination}
                    </h3>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {pkg.tagline}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {pkg.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {pkg.destination}
                    </span>
                    {!pkg.custom && (
                      <span className="flex items-center gap-1">
                        <Bed className="w-3 h-3" />
                        {pkg.nights} Nights
                      </span>
                    )}
                  </div>

                  <ul className="space-y-1.5 flex-1">
                    {(pkg.included || []).map((f) => (
                      <li
                        key={f}
                        className="flex items-center gap-2 text-xs text-gray-600"
                      >
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() =>
                      navigate(`/package-detail?id=${pkg.id}${prefillSuffix}`)
                    }
                    className="mt-2 w-full flex items-center justify-center gap-2 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold py-3 rounded-xl text-sm transition-colors"
                  >
                    Explore Package <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}